/**
 * sl-9qm.4.3: BarChart Component
 *
 * SVG-based bar chart (horizontal or vertical).
 * Aggregation over a column, grouped by another.
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type { ViewComponent } from '../../../types/view-component.types';
import * as styles from '../styles.css';

const configSchema = z.object({
  orientation: z.enum(['vertical', 'horizontal']).default('vertical'),
  groupByColumn: z.string().default(''),
  valueColumn: z.string().default(''),
  aggregationType: z.enum(['count', 'sum', 'avg']).default('count'),
  showLegend: z.boolean().default(true),
  width: z.number().min(240).max(1200).default(400),
  height: z.number().min(160).max(800).default(250),
  marginTop: z.number().min(0).max(120).default(20),
  marginRight: z.number().min(0).max(120).default(20),
  marginBottom: z.number().min(0).max(160).default(40),
  marginLeft: z.number().min(0).max(160).default(50),
});

const CHART_COLORS = [
  'var(--color-chart-1, var(--streamlain-v2-brand-primary))',
  'var(--color-chart-2, #22c7d8)',
  'var(--color-chart-3, #7c5cff)',
  'var(--color-chart-4, #f5b84b)',
  'var(--color-chart-5, #55b86b)',
  'var(--color-chart-6, #e86d8f)',
];

function groupAndAggregate(
  data: Record<string, unknown>[],
  groupBy: string,
  valueCol: string,
  aggType: string
): Array<{ label: string; value: number }> {
  const groups = new Map<string, number[]>();

  for (const row of data) {
    const key = String(row[groupBy] ?? 'Other');
    const val =
      typeof row[valueCol] === 'number'
        ? row[valueCol]
        : parseFloat(String(row[valueCol] ?? 0));
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(isNaN(val as number) ? 0 : (val as number));
  }

  return Array.from(groups.entries()).map(([label, values]) => {
    let value: number;
    switch (aggType) {
      case 'sum':
        value = values.reduce((a, b) => a + b, 0);
        break;
      case 'avg':
        value = values.reduce((a, b) => a + b, 0) / values.length;
        break;
      default:
        value = values.length;
    }
    return { label, value };
  });
}

export const BarChartComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.BAR_CHART,
  type: 'data',
  name: 'Bar Chart',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    '▮'
  ),
  capabilities: [
    {
      role: 'primary',
      description: 'Data rows for aggregation',
      required: true,
      requirements: [
        { field: 'group', acceptedTypes: ['text', 'select'], required: true },
        { field: 'value', acceptedTypes: ['number'], required: false },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    orientation: 'vertical',
    groupByColumn: '',
    valueColumn: '',
    aggregationType: 'count',
    showLegend: true,
    width: 400,
    height: 250,
    marginTop: 20,
    marginRight: 20,
    marginBottom: 40,
    marginLeft: 50,
  },
  render({ config, data }) {
    const {
      orientation,
      groupByColumn,
      valueColumn,
      aggregationType,
      showLegend,
      width,
      height,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
    } = config as z.infer<typeof configSchema>;

    if (data.length === 0 || !groupByColumn) {
      return createElement(
        'div',
        {
          style: {
            padding: 'var(--sl-app-padding-lg)',
            color: 'var(--streamlain-v2-text-secondary)',
            textAlign: 'center' as const,
          },
        },
        'No data — configure groupByColumn'
      );
    }

    const bars = groupAndAggregate(
      data,
      groupByColumn,
      valueColumn,
      aggregationType
    );
    const maxValue = Math.max(...bars.map(b => b.value), 1);
    const margin = {
      top: marginTop,
      right: marginRight,
      bottom: marginBottom,
      left: marginLeft,
    };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const isVertical = orientation === 'vertical';
    const barSize = isVertical ? innerW / bars.length : innerH / bars.length;

    return createElement(
      'div',
      {
        role: 'img',
        'aria-label': `Bar chart: ${bars.map(b => `${b.label}: ${b.value}`).join(', ')}`,
      },
      createElement(
        'svg',
        {
          width,
          height,
          viewBox: `0 0 ${width} ${height}`,
        },
        ...bars.map((bar, i) => {
          const color = CHART_COLORS[i % CHART_COLORS.length];
          if (isVertical) {
            const barH = (bar.value / maxValue) * innerH;
            return createElement('rect', {
              key: i,
              x: margin.left + i * barSize + barSize * 0.1,
              y: margin.top + innerH - barH,
              width: barSize * 0.8,
              height: barH,
              fill: color,
              role: 'graphics-symbol',
              'aria-label': `${bar.label}: ${bar.value}`,
            });
          } else {
            const barW = (bar.value / maxValue) * innerW;
            return createElement('rect', {
              key: i,
              x: margin.left,
              y: margin.top + i * barSize + barSize * 0.1,
              width: barW,
              height: barSize * 0.8,
              fill: color,
              role: 'graphics-symbol',
              'aria-label': `${bar.label}: ${bar.value}`,
            });
          }
        }),
        // X-axis labels (vertical bars)
        ...(isVertical
          ? bars.map((bar, i) =>
              createElement(
                'text',
                {
                  key: `label-${i}`,
                  x: margin.left + i * barSize + barSize / 2,
                  y: height - margin.bottom / 5,
                  textAnchor: 'middle',
                  fontSize: 'var(--sl-app-font-size-xs)',
                  fill: 'var(--streamlain-v2-text-secondary)',
                },
                bar.label.length > 8 ? bar.label.slice(0, 8) + '…' : bar.label
              )
            )
          : [])
      ),
      showLegend &&
        createElement(
          'div',
          {
            style: {
              display: 'flex',
              gap: 'var(--sl-app-gap-sm)',
              flexWrap: 'wrap' as const,
              marginTop: 'var(--sl-app-gap-xs)',
              justifyContent: 'center',
            },
          },
          ...bars.map((bar, i) =>
            createElement(
              'div',
              {
                key: i,
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sl-app-gap-xs)',
                  fontSize: 'var(--sl-app-font-size-xs)',
                },
              },
              createElement('div', {
                style: {
                  width: 12,
                  height: 12,
                  borderRadius: 'var(--sl-app-radius-xs)',
                  background: CHART_COLORS[i % CHART_COLORS.length],
                },
              }),
              bar.label
            )
          )
        )
    );
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '▮ Bar Chart'
    );
  },
};
